/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo } from 'react';
import { Avatar, Empty, Tag, Typography } from 'antd';
import { InboxOutlined, UserOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useQuery } from 'react-query';
import { getEvaluationInstantMessageReceivers } from '~src/services/api/offers';

type MessageRecipient = {
  id: string;
  name: string;
  email?: string;
  company?: string;
  role?: string;
  isPrimary?: boolean;
};

export type RecipientsTabProps = {
  projectId: number;
  tenderId: number;
  contractId: number;
  visible: boolean;
};

const getRecipientInitials = (name: string) => {
  const parts = name
    .split(' ')
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const RecipientsTab: React.FC<RecipientsTabProps> = ({
  projectId,
  tenderId,
  contractId,
  visible,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  // Query receivers
  const { data: receivers, isLoading } = useQuery(
    ['eval_instant_message_receivers', projectId, tenderId, contractId],
    () =>
      getEvaluationInstantMessageReceivers({ projectId, tenderId, contractId }),
    { enabled: !!visible && !!contractId }
  );

  // Map receivers to recipients
  const recipients = useMemo<MessageRecipient[]>(() => {
    if (!receivers?.length) return [];

    return receivers.map((r, i) => ({
      id: r.email || `receiver-${i}`,
      name: r.full_name,
      email: r.email,
      company: r.company_name,
      role: 'Offer recipient',
      isPrimary: i === 0,
    }));
  }, [receivers]);

  return (
    <div css={styles.wrapper}>
      <div css={styles.topNotice}>
        <div css={styles.noticeIcon}>
          <InboxOutlined rev={''} />
        </div>

        <div css={styles.noticeContent}>
          <Typography.Text strong>
            {intl.formatMessage({
              id: 'offers-message-recipients-auto-title',
            })}
          </Typography.Text>

          <Typography.Text type="secondary" css={styles.noticeText}>
            {intl.formatMessage({
              id: 'offers-message-recipients-auto-description',
            })}
          </Typography.Text>
        </div>
      </div>

      <div css={styles.card}>
        <div css={styles.cardHeader}>
          <div>
            <Typography.Title level={5} css={styles.cardTitle}>
              {intl.formatMessage({
                id: 'offers-message-recipients',
              })}
            </Typography.Title>

            <Typography.Text type="secondary" css={styles.cardHint}>
              {intl.formatMessage({
                id: 'offers-message-recipients-hint',
              })}
            </Typography.Text>
          </div>

          <Tag color="blue">
            {recipients.length}{' '}
            {intl.formatMessage({
              id: 'recipients',
            })}
          </Tag>
        </div>

        <div css={styles.recipientsList}>
          {recipients.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({
                id: 'no-data',
              })}
            />
          )}
          {recipients.map((recipient) => (
            <div key={recipient.id} css={styles.recipientRow}>
              <Avatar
                css={styles.recipientAvatar}
                icon={!recipient.name ? <UserOutlined rev={''} /> : undefined}>
                {recipient.name ? getRecipientInitials(recipient.name) : null}
              </Avatar>

              <div css={styles.recipientInfo}>
                <div css={styles.recipientTopLine}>
                  <Typography.Text strong ellipsis>
                    {recipient.name}
                  </Typography.Text>

                  {recipient.isPrimary && (
                    <Tag css={styles.smallTag}>
                      {intl.formatMessage({
                        id: 'primary',
                      })}
                    </Tag>
                  )}
                </div>

                <Typography.Text
                  type="secondary"
                  css={styles.recipientMeta}
                  ellipsis>
                  {recipient.email || '-'}
                  {recipient.company ? ` · ${recipient.company}` : ''}
                </Typography.Text>
              </div>

              <Typography.Text type="secondary" css={styles.roleText}>
                {recipient.role}
              </Typography.Text>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingBottom: 20,
    paddingTop: 2,
  },

  topNotice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 8,
    border: '1px solid #e6f7ff',
    background: '#f6fffb',
  },

  noticeIcon: {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: '50%',
    background: '#e6f7ff',
    color: '#1890ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
  },

  noticeContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },

  noticeText: {
    fontSize: 12,
    lineHeight: 1.45,
  },

  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: 16,
    border: '1px solid #f0f0f0',
    borderRadius: 10,
    background: '#ffffff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
    minHeight: 0,
    height: '100%',
  },

  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },

  cardTitle: {
    margin: 0,
  },

  cardHint: {
    display: 'block',
    fontSize: 12,
    marginTop: 2,
  },

  recipientsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 340,
    overflowY: 'auto',
    paddingRight: 4,
  },

  recipientRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #f5f5f5',
    background: '#fafafa',
    minWidth: 0,
  },

  recipientAvatar: {
    background: '#e6f7ff',
    color: '#1890ff',
    flexShrink: 0,
  },

  recipientInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },

  recipientTopLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },

  recipientMeta: {
    fontSize: 12,
  },

  smallTag: {
    margin: 0,
    fontSize: 11,
    lineHeight: '18px',
  },

  roleText: {
    display: 'none',
    fontSize: 12,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
});
